import { useState } from 'react';

import type { DependencyBlockPayload } from '../../lib/dependency-block.ts';
import { api, ApiError } from '../../live/api.ts';

/**
 * What Start says when it refuses, and what an operator can do about it.
 *
 * The panel exists instead of a red toast because a blocked start is not a mistake the person
 * made: it is the system saying "look at this first". Every row therefore carries the action
 * that resolves it, and the override is one button among several rather than the only way out.
 *
 * The two lists are deliberately not merged. A confirmed link is a decision somebody already
 * made and the only question is whether it still holds; a proposal is a machine's guess and
 * the question is whether it is right at all. Offering "Remove" against a guess, or "Dismiss"
 * against a recorded dependency, would answer the wrong question in each case.
 */
export function StartGatePanel({
  ticketKey,
  block,
  busy,
  onClose,
  onChanged,
  onStartAnyway,
}: {
  ticketKey: string;
  block: DependencyBlockPayload;
  busy: boolean;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
  onStartAnyway: (acknowledge: Array<{ id: number; input_revision: number }>) => void;
}) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settled, setSettled] = useState<Record<number, 'accepted' | 'dismissed'>>({});
  const [removed, setRemoved] = useState<Record<string, true>>({});
  const [sequenced, setSequenced] = useState<string | null>(null);

  const disabled = busy || working;
  const unmet = block.unmet.filter((row) => !removed[row.ticketKey]);
  const proposals = block.proposals.filter((row) => settled[row.id] === undefined);
  const sequencing = block.sequencing.filter((row) => settled[row.id] === undefined);

  async function run(work: () => Promise<unknown>, after: () => void): Promise<void> {
    setWorking(true);
    setError(null);
    try {
      await work();
      after();
      await onChanged();
    } catch (err) {
      // A revision mismatch means the proposal moved while the panel was open. Say so rather
      // than retrying: the fresh evidence may point the other way, and a silent retry would
      // apply a decision to a row the person never read.
      setError(
        err instanceof ApiError && err.status === 409
          ? 'That suggestion changed while this was open. Close this and press Start again to see it.'
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      setWorking(false);
    }
  }

  return (
    <section className="drawer-depblock is-unavailable" aria-label="Start blocked">
      <div className="drawer-depblock-head">
        <span className="drawer-depblock-kicker">Not started</span>
        <button type="button" className="btn btn-sm" onClick={onClose}>
          Close
        </button>
      </div>

      {error ? <p className="drawer-depblock-error">{error}</p> : null}

      {unmet.length > 0 ? (
        <div className="drawer-depblock-group">
          <h4 className="drawer-depblock-subhead">Depends on</h4>
          <ul className="drawer-depblock-list">
            {unmet.map((row) => (
              <li key={row.ticketKey} className="drawer-depblock-row">
                <span className="mono">{row.ticketKey}</span>
                <span className="dim">
                  {row.state}
                  {row.unavailable ? ' · cancelled, so this will never clear' : ''}
                </span>
                {/*
                  The queue exit. Without it this group only offered to delete the dependency
                  or to ignore it, so an operator who agreed with the link had nothing to press
                  and went back to remembering the ticket by hand. Absent on a cancelled
                  blocker: that wait would never end, and the server refuses it.
                */}
                {row.unavailable ? null : (
                  <button
                    type="button"
                    className="btn btn-sm btn-accent"
                    disabled={disabled}
                    onClick={() =>
                      void run(
                        () => api.sequenceAfter(ticketKey, { blocker: row.ticketKey }),
                        () => setSequenced(row.ticketKey),
                      )
                    }
                  >
                    Start after {row.ticketKey} lands
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={disabled}
                  onClick={() =>
                    void run(
                      () => api.removeDependency(ticketKey, row.ticketKey),
                      () => setRemoved((prev) => ({ ...prev, [row.ticketKey]: true })),
                    )
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {block.milestones.length > 0 ? (
        <div className="drawer-depblock-group">
          <h4 className="drawer-depblock-subhead">Earlier milestones not finished</h4>
          <ul className="drawer-depblock-list">
            {block.milestones.map((row) => (
              <li key={row.milestoneId} className="drawer-depblock-row" data-start-gate-milestone={row.milestoneId}>
                <span>{row.name}</span>
                <span className="dim">
                  {row.open.length} open: {row.open.slice(0, 5).map((open) => open.ticketKey).join(', ')}
                  {row.open.length > 5 ? ` and ${row.open.length - 5} more` : ''}
                </span>
              </li>
            ))}
          </ul>
          <p className="drawer-depblock-meta">
            This ticket&apos;s milestone comes after these. Finish their tickets, change the order in
            Settings → Milestones, or start anyway.
          </p>
        </div>
      ) : null}

      {proposals.length > 0 ? (
        <div className="drawer-depblock-group">
          <h4 className="drawer-depblock-subhead">Suggested, not decided</h4>
          <ul className="drawer-depblock-list">
            {proposals.map((row) => (
              <li key={row.id} className="drawer-depblock-row is-proposal">
                <span className="mono">{row.ticketKey}</span>
                <span className="dim clamp-2">{row.reason}</span>
                <span className={`chip chip-band-${row.band}`}>{row.band}</span>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={disabled}
                  onClick={() =>
                    void run(
                      () =>
                        api.decideSuggestion(ticketKey, row.id, 'accept', {
                          expectedInputRevision: row.inputRevision,
                        }),
                      () => setSettled((prev) => ({ ...prev, [row.id]: 'accepted' })),
                    )
                  }
                >
                  Accept and hold
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={disabled}
                  onClick={() =>
                    void run(
                      () =>
                        api.decideSuggestion(ticketKey, row.id, 'dismiss', {
                          expectedInputRevision: row.inputRevision,
                        }),
                      () => setSettled((prev) => ({ ...prev, [row.id]: 'dismissed' })),
                    )
                  }
                >
                  Dismiss
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {sequencing.length > 0 ? (
        <div className="drawer-depblock-group">
          <h4 className="drawer-depblock-subhead">Already being worked on</h4>
          <ul className="drawer-depblock-list">
            {sequencing.map((row) => (
              <li key={row.id} className="drawer-depblock-row is-sequencing">
                <span className="mono">{row.ticketKey}</span>
                <span className="dim drawer-depblock-state">{row.state}</span>
                <span className="dim clamp-2">
                  {row.evidence.length > 0 ? row.evidence.slice(0, 3).join(', ') : row.reason}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-accent"
                  disabled={disabled}
                  onClick={() =>
                    void run(
                      () =>
                        api.sequenceAfter(ticketKey, {
                          blocker: row.ticketKey,
                          suggestionId: row.id,
                          expectedInputRevision: row.inputRevision,
                        }),
                      () => {
                        setSettled((prev) => ({ ...prev, [row.id]: 'accepted' }));
                        setSequenced(row.ticketKey);
                      },
                    )
                  }
                >
                  Start after {row.ticketKey} lands
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {sequenced ? (
        <p className="drawer-depblock-done">
          Parked. This starts on its own once {sequenced} reaches Done.
        </p>
      ) : null}

      {block.scanStatus ? (
        <p className="drawer-depblock-meta">
          Comparison: {block.scanStatus.detail}
        </p>
      ) : null}

      <p className="drawer-depblock-meta">
        Accepting records the dependency and leaves the ticket here until the blocker is Done.
        Starting after a ticket lands parks this one and starts it automatically. Starting
        anyway is recorded against the ticket.
      </p>

      <div style={{ display: 'flex', gap: 7 }}>
        <button
          type="button"
          className="btn btn-sm"
          disabled={disabled}
          onClick={() =>
            onStartAnyway([
              ...block.proposals.map((row) => ({ id: row.id, input_revision: row.inputRevision })),
              // Sequencing rows go in the same list: Start anyway answers them too, and the
              // server settles an overlap it sees acknowledged rather than leaving it to
              // re-block a start that has already happened.
              ...block.sequencing.map((row) => ({ id: row.id, input_revision: row.inputRevision })),
            ])
          }
        >
          Start anyway
        </button>
      </div>
    </section>
  );
}
